import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import Container from "../../components/shared/Container";
import UserSidebar from "../shared/UserSidebar";
import FrontFooter from "../../shared/FrontFooter";
import FrontHeader from "../../shared/FrontHeader";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { setLanguage } from "../../redux/slice/languageSlice";
import { useDispatch } from "react-redux";
import InnerPanel from "../shared/InnerPanel";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { eventDetails } from "../../services/eventService";
import { langConfig } from "../../config/LangConfig";
import { referVerificationDetails } from "../../services/ticketService";
import {
  changeOrganization,
  organizationDetails,
} from "../../services/organizationService";
import { toast } from "react-toastify";
import LoadingFallback from "../../components/elements/LoadingFallback";

const FrontInnerPanelLayout = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(location.search));
  const navigate = useNavigate();
  const { lang: paramsLang } = useParams();
  const { lang } = useSelector((state) => state?.langProvider);
  const { eventId, orgId } = useParams();

  useEffect(() => {
    if (langConfig?.includes(paramsLang)) {
      if (paramsLang !== lang) {
        dispatch(setLanguage(paramsLang));
        localStorage.setItem("lang", paramsLang);
      }
    } else {
      const save_lang = localStorage.getItem("lang");
      let uri_sagement = location?.pathname?.split("/");
      uri_sagement[1] = save_lang || "en";
      navigate(uri_sagement.join("/"));
    }
  }, [paramsLang, lang]);

  const {
    data: eventDetailsData,
    isLoading,
    isFetching,
    refetch: eventDetailsRefetch,
  } = useQuery({
    queryKey: ["event-details", eventId],
    queryFn: async () => {
      const res = await eventDetails({ id: eventId, type: "front" });
      return res;
    },
    enabled: !!eventId,
  });

  const {
    data: isValidOrganization,
    isLoading: isValidOrganizationLoading,
    isFetching: isValidOrganizationFetching,
  } = useQuery({
    queryKey: [
      "check-organization",
      eventDetailsData?.data?.organizationData?._id || orgId,
    ],
    enabled: !!eventDetailsData?.data?.organizationData?._id || !!orgId,
    queryFn: async () => {
      const res = await changeOrganization({
        organization: eventDetailsData?.data?.organizationData?._id || orgId,
        ...(!eventDetailsData?.data?.organizationData?._id && {
          type: "front",
        }),
      });
      if (res?.status === 200) {
        return true;
      } else {
        navigate("/");
        return false;
      }
    },
  });

  const {
    data: organizerDetailsData,
    isLoading: organizerLoading,
    isFetching: organizerFetching,
    refetch: organizationRefetch,
  } = useQuery({
    queryKey: [
      "event-organizer",
      eventDetailsData?.data?.organizationData?._id || orgId,
      isValidOrganization,
    ],
    queryFn: async () => {
      const res = await organizationDetails({
        ...(orgId
          ? { id: orgId, type: "front" }
          : {
              id: eventDetailsData?.data?.organizationData?._id,
            }),
      });

      if (res?.status === 200) {
        return {
          ...res,
          data: {
            ...res?.data,
            themeSetting: {
              ...res?.data?.themeSetting,
              styles: Object.assign(
                ...Object.keys(res?.data?.themeSetting?.styles || {})?.map(
                  (key) => ({
                    [key]: Object.assign(
                      ...res?.data?.themeSetting?.styles[key]?.map((item) => ({
                        [item?.key]: item?.value,
                      })),
                      {}
                    ),
                  })
                ),
                {}
              ),
            },
          },
        };
      } else {
        toast.warning(res?.message);
        return {};
      }
    },
    enabled:
      isValidOrganization === true &&
      (!!eventDetailsData?.data?.organizationData?._id || !!orgId),
  });

  const { data: promoterDetails, isLoading: promoterDetailsIsLoading } =
    useQuery({
      enabled: !!queryObject?.referral_code || !!location?.state?.promoterUid,
      queryKey: [
        "promoter-details",
        queryObject?.referral_code || "",
        location?.state?.promoterUid || "",
        location,
      ],
      queryFn: async () => {
        const res = await referVerificationDetails({
          code: queryObject?.referral_code || location?.state?.promoterUid,
          type: "front",
        });
        if (!res?.data?.verified) {
          if (location?.state?.promoterUid) {
            const state = {
              ...location?.state,
              ...(location?.state?.checkoutData && {
                checkoutData: location?.state?.checkoutData?.map((item) => {
                  return {
                    ...item,
                    price: item?.originalPrice,
                    totalPrice: item?.buy_quentity * item?.originalPrice,
                  };
                }),
              }),
            };
            delete state?.promoterUid;

            navigate(location?.pathname, { state: state });
          }
          if (queryObject?.referral_code) {
            navigate(location?.pathname);
          }
        }
        return res;
      },
    });

  function renderOutlet() {
    return (
      <Outlet
        context={
          eventId
            ? {
                event: eventDetailsData,
                eventLoading: isLoading || isFetching,
                selectedLanguage: lang,
                eventOrganizer: {
                  organizationRefetch,
                  data: organizerDetailsData?.data,
                  loading:
                    isLoading || isFetching
                      ? true
                      : organizerLoading || organizerFetching,
                },
                eventPromoter: {
                  data: promoterDetails,
                  loading: promoterDetailsIsLoading,
                },
                eventDetailsRefetch,
              }
            : {
                organizer: organizerDetailsData,
                loading: organizerLoading || organizerFetching,
                selectedLanguage: lang,
                organizationRefetch,
              }
        }
      />
    );
  }

  return (
    <div className="bg-magnolia">
      <FrontHeader />
      <Container className={"px-0 items-start gap-4"}>
        <div className="w-10 md:w-20 flex-shrink-0 bg-white h-[calc(100vh-6rem)] sticky top-24 z-[40]">
          <UserSidebar />
        </div>
        <div className="w-full max-w-[calc(100%-60px)] lg:max-w-auto flex flex-col lg:flex-row gap-0 lg:gap-0 xl:gap-6">
          <div className="flex-shrink-0 w-full lg:w-64 xl:w-72 py-6 pt-0 h-auto lg:h-[calc(100vh-6rem)] lg:sticky lg:top-24 z-[10]">
            <InnerPanel
              eventDetails={eventDetailsData?.data || {}}
              selectedLanguage={lang}
            />
          </div>
          <div className="w-full flex-shrink py-6 pl-0 lg:px-6 pt-0">
            {isLoading || isValidOrganizationLoading ? (
              <LoadingFallback />
            ) : eventDetailsData?.data?.organizationData?._id || orgId ? (
              isValidOrganization ? (
                renderOutlet()
              ) : null
            ) : (
              renderOutlet()
            )}
          </div>
        </div>
      </Container>
      <FrontFooter />
    </div>
  );
};

export default FrontInnerPanelLayout;
